import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import FilterChip from './components/FilterChip';
import FilterPanel from './components/FilterPanel';
import SortDropdown from './components/SortDropdown';
import ProductGrid from './components/ProductGrid';
import QuickViewModal from './components/QuickViewModal';

const ProductCatalogCategoryBrowse = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);
  const [currentSort, setCurrentSort] = useState('relevance');
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isQuickViewOpen, setIsQuickViewOpen] = useState(false);
  const [filters, setFilters] = useState({
    categories: [],
    brands: [],
    dietary: [],
    priceRange: { min: '', max: '' },
    minRating: 0
  });

  // Mock product data
  const mockProducts = [
    {
      id: 1,
      name: "Fresh Organic Bananas",
      price: 2.99,
      originalPrice: 3.49,
      unit: "per lb",
      image: "https://images.unsplash.com/photo-1571771894821-ce9b6c11b08e?w=400",
      rating: 4.5,
      reviewCount: 128,
      stock: 15,
      isOrganic: true,
      isOnSale: true,
      isNew: false,
      category: "fresh-fruits",
      brand: "organic-valley",
      description: `Fresh, ripe organic bananas perfect for snacking, smoothies, or baking. Rich in potassium and natural sugars for sustained energy.`
    },
    {
      id: 2,
      name: "Premium Strawberries",
      price: 4.99,
      unit: "16 oz container",
      image: "https://images.unsplash.com/photo-1464965911861-746a04b4bca6?w=400",
      rating: 4.8,
      reviewCount: 89,
      stock: 8,
      isOrganic: false,
      isOnSale: false,
      isNew: true,
      category: "fresh-fruits",
      brand: "fresh-farm",
      description: `Sweet, juicy strawberries hand-picked at peak ripeness. Perfect for desserts, breakfast, or healthy snacking.`
    },
    {
      id: 3,
      name: "Organic Baby Spinach",
      price: 3.49,
      unit: "5 oz bag",
      image: "https://images.unsplash.com/photo-1576045057995-568f588f82fb?w=400",
      rating: 4.3,
      reviewCount: 67,
      stock: 12,
      isOrganic: true,
      isOnSale: false,
      isNew: false,
      category: "vegetables",
      brand: "green-choice",
      description: `Tender organic baby spinach leaves, perfect for salads, smoothies, or cooking. Packed with iron and vitamins.`
    },
    {
      id: 4,
      name: "Whole Milk",
      price: 3.99,
      unit: "1 gallon",
      image: "https://images.unsplash.com/photo-1550583724-b2692b85b150?w=400",
      rating: 4.6,
      reviewCount: 234,
      stock: 0,
      isOrganic: false,
      isOnSale: false,
      isNew: false,
      category: "dairy",
      brand: "farm-fresh",
      description: `Fresh whole milk from local dairy farms. Rich, creamy taste perfect for drinking, cereal, or cooking.`
    },
    {
      id: 5,
      name: "Avocados",
      price: 1.99,
      originalPrice: 2.49,
      unit: "each",
      image: "https://images.unsplash.com/photo-1523049673857-eb18f1d7b578?w=400",
      rating: 4.4,
      reviewCount: 156,
      stock: 25,
      isOrganic: false,
      isOnSale: true,
      isNew: false,
      category: "fresh-fruits",
      brand: "nature-best",
      description: `Ripe, creamy avocados perfect for guacamole, toast, or salads. Rich in healthy fats and nutrients.`
    },
    {
      id: 6,
      name: "Organic Chicken Breast",
      price: 8.99,
      unit: "per lb",
      image: "https://images.unsplash.com/photo-1604503468506-a8da13d82791?w=400",
      rating: 4.7,
      reviewCount: 92,
      stock: 6,
      isOrganic: true,
      isOnSale: false,
      isNew: false,
      category: "meat",
      brand: "organic-valley",
      description: `Premium organic chicken breast, free-range and hormone-free. Perfect for grilling, baking, or stir-frying.`
    },
    {
      id: 7,
      name: "Sourdough Bread",
      price: 4.49,
      unit: "1 loaf",
      image: "https://images.unsplash.com/photo-1509440159596-0249088772ff?w=400",
      rating: 4.2,
      reviewCount: 78,
      stock: 4,
      isOrganic: false,
      isOnSale: false,
      isNew: true,
      category: "bakery",
      brand: "fresh-farm",
      description: `Artisan sourdough bread with a crispy crust and tangy flavor. Made with traditional fermentation methods.`
    },
    {
      id: 8,
      name: "Orange Juice",
      price: 3.99,
      unit: "64 fl oz",
      image: "https://images.unsplash.com/photo-1621506289937-a8e4df240d0b?w=400",
      rating: 4.5,
      reviewCount: 145,
      stock: 18,
      isOrganic: false,
      isOnSale: false,
      isNew: false,
      category: "beverages",
      brand: "nature-best",
      description: `Fresh-squeezed orange juice with no added sugars. Rich in vitamin C and natural citrus flavor.`
    },
    {
      id: 9,
      name: "Roma Tomatoes",
      price: 2.49,
      unit: "per lb",
      image: "https://images.unsplash.com/photo-1546470427-e26264be0b0d?w=400",
      rating: 4.1,
      reviewCount: 103,
      stock: 22,
      isOrganic: false,
      isOnSale: false,
      isNew: false,
      category: "vegetables",
      brand: "fresh-farm",
      description: `Fresh Roma tomatoes perfect for cooking, sauces, or salads. Firm texture and rich flavor.`
    },
    {
      id: 10,
      name: "Greek Yogurt",
      price: 5.99,
      unit: "32 oz container",
      image: "https://images.unsplash.com/photo-1488477181946-6428a0291777?w=400",
      rating: 4.6,
      reviewCount: 187,
      stock: 14,
      isOrganic: true,
      isOnSale: false,
      isNew: false,
      category: "dairy",
      brand: "organic-valley",
      description: `Creamy organic Greek yogurt with live cultures. High in protein and perfect for breakfast or snacks.`
    },
    {
      id: 11,
      name: "Blueberries",
      price: 3.99,
      unit: "6 oz container",
      image: "https://images.unsplash.com/photo-1498557850523-fd3d118b962e?w=400",
      rating: 4.7,
      reviewCount: 156,
      stock: 11,
      isOrganic: true,
      isOnSale: false,
      isNew: false,
      category: "fresh-fruits",
      brand: "green-choice",
      description: `Sweet, plump organic blueberries packed with antioxidants. Perfect for breakfast, baking, or snacking.`
    },
    {
      id: 12,
      name: "Bell Peppers Mix",
      price: 4.99,
      unit: "3-pack",
      image: "https://images.unsplash.com/photo-1525607551862-4d0b24fa2a2b?w=400",
      rating: 4.3,
      reviewCount: 89,
      stock: 16,
      isOrganic: false,
      isOnSale: false,
      isNew: false,
      category: "vegetables",
      brand: "fresh-farm",
      description: `Colorful mix of red, yellow, and green bell peppers. Crisp and sweet, perfect for cooking or raw snacking.`
    }
  ];

  // Load products on component mount
  useEffect(() => {
    const loadProducts = async () => {
      setIsLoading(true);
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      setProducts(mockProducts);
      setIsLoading(false);
    };

    loadProducts();
  }, []);

  // Filter and sort products
  useEffect(() => {
    let filtered = [...products];

    // Apply search filter
    if (searchQuery) {
      filtered = filtered.filter(product =>
        product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        product.description.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Apply category filter
    if (filters.categories.length > 0) {
      filtered = filtered.filter(product =>
        filters.categories.includes(product.category)
      );
    }

    // Apply brand filter
    if (filters.brands.length > 0) {
      filtered = filtered.filter(product =>
        filters.brands.includes(product.brand)
      );
    }

    // Apply dietary filter
    if (filters.dietary.length > 0) {
      filtered = filtered.filter(product => {
        return filters.dietary.some(diet => {
          if (diet === 'organic') return product.isOrganic;
          if (diet === 'gluten-free') return product.isGlutenFree;
          if (diet === 'vegan') return product.isVegan;
          if (diet === 'non-gmo') return product.isNonGMO;
          if (diet === 'keto') return product.isKeto;
          return false;
        });
      });
    }

    // Apply price range filter
    if (filters.priceRange.min || filters.priceRange.max) {
      filtered = filtered.filter(product => {
        const price = product.price;
        const min = filters.priceRange.min ? parseFloat(filters.priceRange.min) : 0;
        const max = filters.priceRange.max ? parseFloat(filters.priceRange.max) : Infinity;
        return price >= min && price <= max;
      });
    }

    // Apply rating filter
    if (filters.minRating > 0) {
      filtered = filtered.filter(product => product.rating >= filters.minRating);
    }

    // Apply sorting
    switch (currentSort) {
      case 'price-low-high':
        filtered.sort((a, b) => a.price - b.price);
        break;
      case 'price-high-low':
        filtered.sort((a, b) => b.price - a.price);
        break;
      case 'rating':
        filtered.sort((a, b) => b.rating - a.rating);
        break;
      case 'newest':
        filtered.sort((a, b) => b.isNew - a.isNew);
        break;
      case 'name-az':
        filtered.sort((a, b) => a.name.localeCompare(b.name));
        break;
      case 'name-za':
        filtered.sort((a, b) => b.name.localeCompare(a.name));
        break;
      default:
        // Relevance - keep original order
        break;
    }

    setFilteredProducts(filtered);
  }, [products, searchQuery, filters, currentSort]);

  const handleSearch = (e) => {
    e.preventDefault();
    const params = new URLSearchParams(searchParams);
    if (searchQuery) {
      params.set('search', searchQuery);
    } else {
      params.delete('search');
    }
    setSearchParams(params);
  };

  const handleAddToCart = useCallback(async (product) => {
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 500));
    
    const existingCart = JSON.parse(localStorage.getItem('cart') || '[]');
    const existingItem = existingCart.find(item => item.id === product.id);
    
    if (existingItem) {
      existingItem.quantity += product.quantity || 1;
    } else {
      existingCart.push({
        id: product.id,
        name: product.name,
        price: product.price,
        image: product.image,
        unit: product.unit,
        quantity: product.quantity || 1
      });
    }
    
    localStorage.setItem('cart', JSON.stringify(existingCart));
    
    // Trigger cart update event
    window.dispatchEvent(new Event('cartUpdated'));
  }, []);

  const handleQuickView = (product) => {
    setSelectedProduct(product);
    setIsQuickViewOpen(true);
  };

  const getActiveFilters = () => {
    const active = [];
    
    if (filters.categories.length > 0) {
      active.push({
        type: 'categories',
        label: `Categories (${filters.categories.length})`,
        count: filters.categories.length
      });
    }
    
    if (filters.brands.length > 0) {
      active.push({
        type: 'brands',
        label: `Brands (${filters.brands.length})`,
        count: filters.brands.length
      });
    }
    
    if (filters.dietary.length > 0) {
      active.push({
        type: 'dietary',
        label: `Dietary (${filters.dietary.length})`,
        count: filters.dietary.length
      });
    }
    
    if (filters.priceRange.min || filters.priceRange.max) {
      const min = filters.priceRange.min || '0';
      const max = filters.priceRange.max || '∞';
      active.push({
        type: 'priceRange',
        label: `$${min} - $${max}`,
        count: 1
      });
    }
    
    if (filters.minRating > 0) {
      active.push({
        type: 'minRating',
        label: `${filters.minRating}+ Stars`,
        count: 1
      });
    }
    
    return active;
  };

  const removeFilter = (filterType) => {
    setFilters(prev => {
      const newFilters = { ...prev };
      if (filterType === 'priceRange') {
        newFilters.priceRange = { min: '', max: '' };
      } else if (filterType === 'minRating') {
        newFilters.minRating = 0;
      } else {
        newFilters[filterType] = [];
      }
      return newFilters;
    });
  };

  const clearAllFilters = () => {
    setFilters({
      categories: [],
      brands: [],
      dietary: [],
      priceRange: { min: '', max: '' },
      minRating: 0
    });
    setSearchQuery('');
    const params = new URLSearchParams(searchParams);
    params.delete('search');
    setSearchParams(params);
  };

  const activeFilters = getActiveFilters();

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="pt-16 lg:pt-18">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <Breadcrumb customItems={[
            { label: 'Home', path: '/homepage-dashboard' },
            { label: 'Products', path: '/product-catalog-category-browse' }
          ]} />

          <div className="flex flex-col lg:flex-row gap-6">
            {/* Desktop Filter Sidebar */}
            <div className="hidden lg:block w-80 flex-shrink-0">
              <FilterPanel
                isOpen={true}
                filters={filters}
                onFiltersChange={setFilters}
                isMobile={false}
              />
            </div>

            {/* Main Content */}
            <div className="flex-1">
              {/* Mobile Search */}
              <div className="lg:hidden mb-6">
                <form onSubmit={handleSearch}>
                  <div className="relative">
                    <Input
                      type="search"
                      placeholder="Search products..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full pl-10 pr-4 py-3"
                    />
                    <Icon
                      name="Search"
                      size={20}
                      className="absolute left-3 top-1/2 transform -translate-y-1/2 text-text-secondary"
                    />
                  </div>
                </form>
              </div>

              {/* Filter Controls */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <Button
                    variant="outline"
                    onClick={() => setIsFilterPanelOpen(true)}
                    className="lg:hidden"
                    iconName="Filter"
                    iconSize={20}
                  >
                    Filters
                  </Button>
                  
                  {activeFilters.length > 0 && (
                    <Button
                      variant="text"
                      onClick={clearAllFilters}
                      className="text-sm"
                    >
                      Clear All
                    </Button>
                  )}
                </div>
              </div>

              {/* Active Filter Chips */}
              {activeFilters.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-6">
                  {activeFilters.map((filter, index) => (
                    <FilterChip
                      key={index}
                      label={filter.label}
                      count={filter.count}
                      onRemove={() => removeFilter(filter.type)}
                    />
                  ))}
                </div>
              )}

              {/* Sort and Results */}
              <SortDropdown
                currentSort={currentSort}
                onSortChange={setCurrentSort}
                resultsCount={filteredProducts.length}
              />

              {/* Product Grid */}
              <ProductGrid
                products={filteredProducts}
                onAddToCart={handleAddToCart}
                onQuickView={handleQuickView}
                isLoading={isLoading}
              />

              {/* Load More Button */}
              {!isLoading && filteredProducts.length > 0 && (
                <div className="flex justify-center mt-8">
                  <Button
                    variant="outline"
                    className="px-8"
                  >
                    Load More Products
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      {/* Mobile Filter Panel */}
      <FilterPanel
        isOpen={isFilterPanelOpen}
        onClose={() => setIsFilterPanelOpen(false)}
        filters={filters}
        onFiltersChange={setFilters}
        isMobile={true}
      />

      {/* Quick View Modal */}
      <QuickViewModal
        product={selectedProduct}
        isOpen={isQuickViewOpen}
        onClose={() => setIsQuickViewOpen(false)}
        onAddToCart={handleAddToCart}
      />
    </div>
  );
};

export default ProductCatalogCategoryBrowse;